<script lang="ts" setup>import { ref as _ref, toRef as _toRef, computed as _computed } from 'vue';

import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import { trackClick, trackEvent, trackModal } from '@shared/tracking'
import { templateRef, whenever } from '@vueuse/core'
import { modalDropdown } from '@consumer/utils/transitions'
import { useListeners } from '@shared/vue'
import { isFunction } from '@shared/functions'
import { faClose } from '@fortawesome/pro-regular-svg-icons'
import { faChevronLeft, faXmark } from '@fortawesome/pro-light-svg-icons'
import { isMobile } from '@consumer/utils/breakpoints'
import { preventWindowScroll, useVisualViewport } from '@shared/composables'
import type { ClassValue } from 'vue'
import type GIconButton from './GIconButton.vue'

defineOptions({
  inheritAttrs: false,
})

withDefaults(defineProps<{
  name: string
  appear?: boolean
  persistent?: boolean // If true, modal cannot be dismissed by clicking outside or hitting ESC
  fullscreenMobile?: boolean
  fullscreen?: boolean
  hideCloseButton?: boolean
  disableClose?: boolean
  noPadding?: boolean
  centered?: boolean
  width?: 'narrow' | 'wide' | 'mediumwide' | 'superwide' | 'ultrawide' | 'unconstrained'
  // Lets the header slot span the modal edge-to-edge (for headers with their own
  // background), floating the back/close buttons over it instead of reserving space.
  fullBleedHeader?: boolean
  headerClass?: ClassValue
  containerClass?: string
  useActionSheet?: boolean
  withKeyboardPadding?: boolean
  onBack?: (closeModal?: () => void) => void
}>(), { persistent: false,fullscreenMobile: false,fullscreen: false,noPadding: false,width: 'unconstrained',withKeyboardPadding: true,useActionSheet: false,fullBleedHeader: false, })

let open = (defineModel<boolean>('open', { type: null }))

if (open.value === undefined) {
  open.value = false
  // Setting open to true on the next tick to allow for transition animations
  setTimeout(() => {
    open.value = true
  }, 0)
}

const hasCloseListener = useListeners(listeners => isFunction(listeners.onClose))

const emit = defineEmits<{ close: [closeModal: Function]; remove: [] }>()

const closeButtonEl = _ref<InstanceType<typeof GIconButton>>()
const contentWrapper = templateRef<HTMLElement>('contentWrapper')
const __$temp_1 = (useVisualViewport()),
  keyboardHeight = _toRef(__$temp_1, 'keyboardHeight'),
  isKeyboardOpen = _toRef(__$temp_1, 'isKeyboardOpen');

const keyboardPadding = _computed(() => {
  if (!__props.withKeyboardPadding) return
  if (!isKeyboardOpen.value) return

  return keyboardHeight.value + 260
})

// Emit an event to notify that the user intends to close the modal.
//
// NOTE: When the parent component adds a @close listener, it must use the
// callback to manually close the modal.
function stopAndCloseModal () {
  if (hasCloseListener) {
    emit('close', () => { open.value = false })
  }
  else {
    open.value = false
  }
}

// 'reset' is emitted by form elements when GCancelButton is clicked.
//
// NOTE: By leveraging the native 'reset' event, there is no need to manually
// add a close listener on every modal forms.
function onFormReset () {
  stopAndCloseModal()
}

function closeModal () {
  trackEvent('click', closeButtonEl.value?.$el)
  stopAndCloseModal()
}

function onClickOutside () {
  if (__props.persistent || __props.disableClose) return

  trackEvent('click', `close-modal-outside-${__props.name}`)
  stopAndCloseModal()
}

function scrollTop () {
  if (contentWrapper.value) {
    contentWrapper.value.scrollTop = 0
  }
}

const slots = useSlots()

const isMobileFullscreen = computed(() => isMobile.value && __props.fullscreenMobile)
const isMobileActionSheet = _computed(() => isMobile.value && __props.useActionSheet)

// Prevent window scroll when the modal is open (for mobile action sheets)
preventWindowScroll({ when: () => !!document.querySelector('dialog.fullscreen[open]') })

whenever(
  () => open.value && isMobileActionSheet.value,
  () => setTimeout(scrollTop, 100),
)

watchEffect(() => {
  if (isMobileActionSheet.value && (!isKeyboardOpen.value || !__props.withKeyboardPadding)) scrollTop()
})

whenever(() => open.value, () => trackModal(__props.name), { immediate: true })

defineExpose({
  scrollTop,
})
</script>

<template>
  <!-- Mobile Action Sheet -->
  <TransitionRoot v-if="isMobileActionSheet" :show="open">
    <dialog
      class="fixed inset-0 w-full h-full bg-navy bg-opacity-0 z-dialog fullscreen g-mobile-modal"
      :class="containerClass"
      open
    >
      <div class="relative w-full h-full">
        <TransitionChild
          as="template"
          enter="transition-opacity ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in-out duration-700"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div class="absolute inset-0 bg-navy bg-opacity-40" @click="closeModal"/>
        </TransitionChild>
        <TransitionChild
          as="template"
          enter="transition ease-in-out duration-300 transform"
          enterFrom="translate-y-full scale-y-0"
          enterTo="translate-y-0 scale-y-100"
          leave="transition ease-in-out duration-500 transform"
          leaveFrom="translate-y-0 scale-y-100"
          leaveTo="translate-y-full scale-y-0"
          @afterLeave="emit('remove')"
        >
          <div
            ref="contentWrapper"
            class="
              absolute bottom-0 w-full bg-white rounded-t-v2lg
              overflow-y-auto transition-all modal-content-wrapper
            "
            style="max-height: calc(100svh - 1rem);"
            v-bind="$attrs"
            @click="trackClick"
            @reset="onFormReset"
          >
            <GIconButton
              v-if="!hideCloseButton"
              ref="closeButtonEl"
              :icon="faXmark"
              type="button"
              size="small"
              class="absolute right-2 outline-none ring-0 z-10 top-5"
              @click="closeModal"
            />

            <GIconButton
              v-if="onBack"
              :icon="faChevronLeft"
              size="small"
              class="absolute left-2 outline-none ring-0 z-10 top-5"
              @click="() => onBack && onBack(closeModal)"
            />

            <div
              v-if="slots.header"
              class="w-full pb-8 flex items-center justify-center font-semibold text-lg pt-4"
              :class="headerClass"
            >
              <slot name="header"/>
            </div>

            <div
              class="px-4 py-8 transition-all duration-300 ease-in-out modal-body g-mobile-modal-body"
              :class="{ '!pt-0': slots.header, '!p-0': noPadding }"
              :style="keyboardPadding ? `padding-bottom: ${keyboardPadding}px` : ''"
            >
              <slot/>
            </div>
            <slot name="actions"/>
            <button class="opacity-0 absolute bottom-0"/>
          </div>
        </TransitionChild>
      </div>
    </dialog>
  </TransitionRoot>

  <!-- Desktop Modal -->
  <TransitionRoot v-else as="template" :show="open" :appear="appear" @afterLeave="emit('remove')">
    <Dialog
      as="div"
      class="
        g-dialog
        blur-area fixed
        inset-0 overflow-y-auto
        dialog break-word
        grid py-8
        grid-cols-1
        justify-center
        justify-items-center
        z-dialog
      "
      :class="[{
        '!p-0': fullscreenMobile,
        '!lg:p-0': fullscreen,
        '!bg-white !bg-opacity-100': isMobile && fullscreenMobile,
        'content-center': centered && !fullscreenMobile,
        'content-start': !centered && !fullscreenMobile,
      }, containerClass]"
      :initialFocus="closeButtonEl?.$el"
      :static="true"
      @close="onClickOutside"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-200"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-40"/>
      </TransitionChild>

      <TransitionChild as="template" :appear="appear" v-bind="modalDropdown">
        <DialogPanel
          as="div"
          class="
            g-dialog-panel
            relative mx-auto
            bg-white border-solid border-1 border-grey-primary
            flex flex-col justify-evenly
          "
          :class="[
            width,
            isMobileFullscreen
              ? 'fixed inset-0 max-w-full h-full !border-0 flex-grow'
              : 'max-w-440px',
            fullscreen
              ? 'fixed inset-0 lg:max-w-full h-full rounded-none'
              : 'rounded-v2lg lg:max-w-screen-lg',
            noPadding && 'rounded-v2lg',
            fullBleedHeader && 'overflow-hidden',
          ]"
          v-bind="$attrs"
          aria-hidden="false"
          aria-modal="true"
          @click="trackClick"
        >
          <div
            class="w-full flex flex-row items-center justify-between"
          >
            <!-- Have the width be fixed so that the title is always centered -->
            <GIconButton
              v-if="onBack"
              id="back-button"
              class="g-dialog-close absolute left-3 top-6 z-dialog"
              :disabled="disableClose"
              :icon="faChevronLeft"
              size="medium"
              label="Back"
              @click="() => onBack && onBack(closeModal)"
            />

            <DialogTitle
              v-if="slots.header"
              class="g-dialog-header font-semibold text-lg p-6 g-dialog-title w-full text-center"
              :class="headerClass"
            >
              <slot name="header"/>
            </DialogTitle>

            <!-- Have the width be fixed so that the title is always centered -->
            <GIconButton
              v-if="!hideCloseButton"
              id="close-modal-button"
              ref="closeButtonEl"
              :name="`close-modal-icon-button-${name}`"
              class="g-dialog-close absolute right-3 top-6 z-dialog"
              :disabled="disableClose"
              :icon="faClose"
              size="medium"
              label="Close Modal"
              @click="closeModal"
            />
          </div>

          <div
            class="flex-auto flex flex-col g-dialog-content"
            :class="{'<lg:h-full' : fullscreenMobile}"
            @reset="onFormReset"
          >
            <div class="h-full p-6 g-dialog-content-wrapper" :class="{'!p-0': noPadding}">
              <slot/>
            </div>
          </div>
          <slot name="actions"/>
        </DialogPanel>
      </TransitionChild>
      <button class="opacity-0 absolute bottom-0"/>
    </Dialog>
  </TransitionRoot>
</template>

<style lang="scss">
.g-dialog {
  .g-dialog-panel {
    box-shadow: 0 4px 20px rgb(0 0 0 / 15%);
    max-width: 100vw;
  }

  .g-button-large:not(.g-button-no-label):not(.g-button-round) {
    width: 100%;
  }
}

.narrow {
  width: 350px;
}

.wide {
  width: 398px;
}

.mediumwide {
  width: 495px;
}

.superwide {
  width: 600px;
}

.ultrawide {
  width: 800px;
}

#close-modal-button:focus:not(:hover, :active) {
  --color-focus: var(--color);
  --bg-focus: var(--bg);
}
</style>
